// BotAppearance.tsx — dashboard bot Appearance screen (M4 task-6, route
// /bots/:botId/appearance). Edits `BotSettings.appearance` AND `BotSettings.language`
// (packages/shared-types/settings.ts; the latter added in Task 3 of the widget-i18n cycle) via
// `PATCH /api/bots/:id { settings: { appearance, language } }` — replace-per-top-level-key
// semantics (apps/api/src/routes/bots.ts), so this always sends the FULL appearance object
// (never a partial patch of it) and the current `language` value alongside it. `botSettingsSchema`
// (packages/validation, imported directly — dashboard has it as a workspace dep) doubles as the
// client-side "parse defaults" helper: `BotDto.settings` is `unknown` on the wire, and parsing it
// here is exactly how the server derives defaults too, so the two can never drift.
import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { botSettingsSchema } from '@repo/validation';
import { Button } from '@repo/ui/components/button';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { Switch } from '@repo/ui/components/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import {
  PLAN_CODES,
  PLAN_LIMITS,
  resolveShowsBadge,
  UNLIMITED_PLAN_FACTS,
  WIDGET_LOCALES,
  WIDGET_LOCALE_LABELS,
} from '@repo/shared-types';
import type {
  BotAppearanceSettings,
  BotDto,
  BotLanguageSetting,
  BotSettingsPosition,
} from '@repo/shared-types';
import { PageHeader } from '../components/PageHeader';
import { WidgetPreview } from '../components/WidgetPreview';
import { apiFetch } from '../lib/api';
import { useBillingSummary } from '../hooks/useBillingSummary';
import { useT } from '../locales/context';
import type { Catalog } from '../locales/en';

const MAX_QUICK_QUESTIONS = 4;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function parseSettings(raw: unknown) {
  return botSettingsSchema.parse(raw ?? {});
}

/** Plan names for which the badge is optional rather than required — derived from
 * `PLAN_LIMITS`, never hand-written (badge opt-in spec, 2026-08-11, ambiguity resolution #3:
 * "do not hand-write a plan name into it"). Feeds the Free note below. */
const OPTIONAL_BADGE_PLAN_NAMES = PLAN_CODES.filter((code) => !PLAN_LIMITS[code].badgeRequired).map(
  (code) => PLAN_LIMITS[code].name,
);

/** "A", "A or B", "A, B, or C" — small enough to not need a library, general enough for the
 * plan list above to grow past two without this needing a second look. Pure formatting, not
 * copy — the joining words ("or"/", or ") stay hand-written here rather than in the catalog,
 * same footing as the `n === 1 ? '' : 's'` plural branches already elsewhere in this codebase. */
function formatOrList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} or ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}`;
}

function getPositionOptions(t: Catalog): { value: BotSettingsPosition; label: string }[] {
  return [
    { value: 'bottom-right', label: t.appearance.positionBottomRight },
    { value: 'bottom-left', label: t.appearance.positionBottomLeft },
  ];
}

// Derived from `WIDGET_LOCALES`, not hand-listed — ADDING a third language needs an entry in
// `WIDGET_LOCALES` and `WIDGET_LOCALE_LABELS` (both `packages/shared-types/src/settings.ts`)
// and nothing else here. REMOVING one is not the same operation in reverse: a bot pinned to the
// locale being dropped keeps a stored value this select can no longer render as an option —
// see `WIDGET_LOCALES`'s own doc comment for why that degrades safely (to 'auto') instead of
// breaking the bot, and check for bots still pinned to it before removing.
//
// 'auto' isn't a `WidgetLocale`, so it isn't in `WIDGET_LOCALE_LABELS` — `t.appearance.
// autoLanguageLabel` is dashboard chrome, written once, in plain words (this screen doesn't use
// exclamation marks or marketing copy). Review round 2 (Minor): the original wording ("Match
// each visitor's browser language", 37 chars) ran past the `w-64` trigger's `line-clamp-1`
// width and would have truncated for every owner, since 'auto' is the default every bot starts
// on. Shortened; the trigger below was also widened to `w-72` as a second margin of safety, not
// a substitute for keeping this short. `WIDGET_LOCALE_LABELS` entries themselves are endonyms
// naming the bot's answer language, not dashboard chrome, and stay untouched (locale-dashboard
// plan, Task 11 scope fence).
function getLanguageOptions(t: Catalog): { value: BotLanguageSetting; label: string }[] {
  return [
    { value: 'auto', label: t.appearance.autoLanguageLabel },
    ...WIDGET_LOCALES.map((locale) => ({ value: locale, label: WIDGET_LOCALE_LABELS[locale] })),
  ];
}

/** Sized to `AppearanceForm`'s real two-column layout — field blocks on the left, the live
 * preview panel on the right (handoff Shell Anatomy §06). */
function AppearanceSkeleton() {
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      <div className="flex flex-col gap-5 rounded-xl border p-4">
        {['w-14', 'w-20', 'w-32'].map((w) => (
          <div key={w} className="flex flex-col gap-2">
            <SkeletonText className={w} />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
}

export function BotAppearance() {
  const { botId } = useParams<{ botId: string }>();
  const t = useT();

  const {
    data: bot,
    isPending: botPending,
    error: botFetchError,
  } = useQuery({
    queryKey: ['bots', botId],
    queryFn: () => apiFetch<BotDto>(`/api/bots/${botId}`),
    enabled: !!botId,
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t.appearance.pageTitle} subtitle={t.appearance.pageSubtitle} />

      {botPending && <AppearanceSkeleton />}
      {botFetchError && <p className="text-destructive">{botFetchError.message}</p>}

      {/* `key={bot.id}` makes the form's initial state (below, via lazy useState
          initializers) derive fresh from `bot.settings` exactly once per bot — no effect
          needed to sync it in, and a background refetch (e.g. the invalidate this same form
          triggers after a successful save) can't clobber in-progress edits since the key
          doesn't change. */}
      {bot && <AppearanceForm key={bot.id} botId={botId} bot={bot} />}
    </div>
  );
}

function AppearanceForm({ botId, bot }: { botId: string | undefined; bot: BotDto }) {
  const t = useT();
  const queryClient = useQueryClient();
  const parsed = parseSettings(bot.settings);
  const initial = parsed.appearance;
  const positionOptions = getPositionOptions(t);
  const languageOptions = getLanguageOptions(t);
  // One line, factual, no exclamation (ambiguity resolution #3) — names what removes the
  // REQUIREMENT, not what removes the badge itself: a paid plan's badge is optional, off by
  // default, not automatically gone (see `PlanFacts.badgeRequired`'s own doc comment).
  const freeBadgeNote = t.appearance.freeBadgeNote({ plans: formatOrList(OPTIONAL_BADGE_PLAN_NAMES) });

  const [color, setColor] = useState(initial.color);
  const [hexInput, setHexInput] = useState(initial.color);
  const [botName, setBotName] = useState(initial.botName);
  const [welcome, setWelcome] = useState(initial.welcome);
  const [quickQuestions, setQuickQuestions] = useState<string[]>(initial.quickQuestions);
  const [position, setPosition] = useState<BotSettingsPosition>(initial.position);
  const [showBadge, setShowBadge] = useState(parsed.showBadge);
  const [language, setLanguage] = useState<BotLanguageSetting>(parsed.language);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  // Workspace-level, not per-bot (same query the Billing screen and the usage rail share —
  // React Query dedupes under `BILLING_SUMMARY_QUERY_KEY`), needed here only for
  // `badgeRequired`: the plan fact, never the rendered outcome. Resolved the same way the
  // server does (`apps/api/src/lib/plan.ts`'s `getWorkspaceLimits`: `internal ?
  // UNLIMITED_PLAN_FACTS : PLAN_LIMITS[plan]`) rather than hand-writing `internal ? false :
  // ...` here — `UNLIMITED_PLAN_FACTS.badgeRequired` is its own decision (see that constant's
  // doc comment, which reasons specifically about whether internal bots should be required to
  // carry the badge), and re-deciding it client-side is exactly the kind of duplication that
  // drifts the moment someone answers the question that comment poses.
  const { data: billingSummary, error: billingSummaryError } = useBillingSummary();
  const badgeFacts = billingSummary
    ? billingSummary.internal
      ? UNLIMITED_PLAN_FACTS
      : PLAN_LIMITS[billingSummary.plan.key]
    : undefined;
  // `undefined` while pending or on a failed fetch — the badge control renders a loading
  // placeholder / error rather than guess a plan (badge opt-in spec, 2026-08-11).
  const badgeRequired = badgeFacts?.badgeRequired;

  function handleColorPicker(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setColor(value);
    setHexInput(value);
  }

  function handleHexChange(e: React.ChangeEvent<HTMLInputElement>) {
    setHexInput(e.target.value);
  }

  function handleHexBlur() {
    const trimmed = hexInput.trim();
    const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    if (HEX_RE.test(withHash)) {
      const normalized = withHash.toLowerCase();
      setColor(normalized);
      setHexInput(normalized);
    } else {
      setHexInput(color);
    }
  }

  function updateQuickQuestion(index: number, value: string) {
    setQuickQuestions((prev) => prev.map((q, i) => (i === index ? value : q)));
  }

  function removeQuickQuestion(index: number) {
    setQuickQuestions((prev) => prev.filter((_, i) => i !== index));
  }

  function addQuickQuestion() {
    setQuickQuestions((prev) => (prev.length >= MAX_QUICK_QUESTIONS ? prev : [...prev, '']));
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const appearance: BotAppearanceSettings = {
        color,
        botName: botName.trim(),
        welcome,
        quickQuestions: quickQuestions.map((q) => q.trim()).filter((q) => q.length > 0),
        position,
      };
      const settings: Record<string, unknown> = { appearance, language };
      // Ambiguity resolution #1 (task-2-brief.md): the Free branch renders the switch on and
      // disabled but must NEVER submit `showBadge: true` — an ordinary controlled switch just
      // echoes whatever it displays, so a naive save here would resend the disabled-on value
      // on every Appearance save. `apps/api/src/routes/bots.ts`'s PATCH handler forces
      // `showBadge: false` server-side whenever the plan requires the badge, but that rail is
      // the backstop, not the mechanism — this key is omitted entirely (not sent as `false`
      // either) whenever `badgeRequired` isn't known to be `false`, so a Free/still-loading
      // save can never carry an opinion about a setting the owner has no control over.
      if (badgeRequired === false) settings.showBadge = showBadge;
      return apiFetch<BotDto>(`/api/bots/${botId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings }),
      });
    },
    onSuccess: async () => {
      setSaveError(null);
      setSaved(true);
      setPreviewKey((k) => k + 1);
      await queryClient.invalidateQueries({ queryKey: ['bots', botId] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          saveMutation.mutate();
        }}
        className="flex flex-col gap-5 rounded-xl border p-4"
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="appearance-color">{t.appearance.colorLabel}</Label>
          <div className="flex items-center gap-2">
            <input
              id="appearance-color"
              type="color"
              value={color}
              onChange={handleColorPicker}
              className="h-9 w-12 cursor-pointer rounded-md border border-input p-1"
            />
            <Input
              aria-label={t.appearance.colorHexAriaLabel}
              value={hexInput}
              onChange={handleHexChange}
              onBlur={handleHexBlur}
              maxLength={7}
              className="w-32 font-mono"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="appearance-bot-name">{t.appearance.botNameLabel}</Label>
          <Input
            id="appearance-bot-name"
            value={botName}
            onChange={(e) => setBotName(e.target.value)}
            maxLength={40}
            placeholder={t.appearance.botNamePlaceholder}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="appearance-welcome">{t.appearance.welcomeMessageLabel}</Label>
          <textarea
            id="appearance-welcome"
            value={welcome}
            onChange={(e) => setWelcome(e.target.value)}
            maxLength={300}
            rows={3}
            placeholder={t.appearance.welcomePlaceholder}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Label>{t.appearance.quickQuestionsLabel}</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={quickQuestions.length >= MAX_QUICK_QUESTIONS}
              onClick={addQuickQuestion}
            >
              {t.appearance.addQuestionCta}
            </Button>
          </div>
          {quickQuestions.length === 0 && (
            <p className="text-sm text-muted-foreground">{t.appearance.quickQuestionsHint}</p>
          )}
          {quickQuestions.map((question, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                aria-label={t.appearance.quickQuestionAriaLabel({ n: i + 1 })}
                value={question}
                onChange={(e) => updateQuickQuestion(i, e.target.value)}
                maxLength={80}
                placeholder={t.appearance.quickQuestionPlaceholder}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeQuickQuestion(i)}
              >
                {t.appearance.removeCta}
              </Button>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="appearance-position">{t.appearance.positionLabel}</Label>
          <Select
            value={position}
            onValueChange={(value) => setPosition(value as BotSettingsPosition)}
          >
            <SelectTrigger id="appearance-position" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {positionOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="appearance-language">{t.appearance.languageLabel}</Label>
          <Select
            value={language}
            onValueChange={(value) => setLanguage(value as BotLanguageSetting)}
          >
            <SelectTrigger id="appearance-language" className="w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languageOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2 border-t border-border pt-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              {/* No `htmlFor` — this label pairs with the switch below only when the switch
                  itself is what's rendered on the right (badge opt-in spec fix round, Minor
                  4: a `Skeleton`/error placeholder has no matching id). The switch's own
                  `aria-label` already gives it a real accessible name either way. */}
              <Label>{t.appearance.badgeLabel}</Label>
              <span className="text-xs text-muted-foreground">{t.appearance.badgeHint}</span>
            </div>
            {badgeRequired === undefined ? (
              billingSummaryError ? (
                <span className="text-xs text-destructive">{t.appearance.badgeLoadError}</span>
              ) : (
                <Skeleton className="h-[1.15rem] w-8 rounded-full" />
              )
            ) : (
              <Switch
                aria-label={t.appearance.badgeSwitchAriaLabel}
                checked={resolveShowsBadge({ badgeRequired, showBadge })}
                disabled={badgeRequired}
                onCheckedChange={badgeRequired ? undefined : setShowBadge}
              />
            )}
          </div>
          {badgeRequired && <p className="text-xs text-muted-foreground">{freeBadgeNote}</p>}
        </div>

        {saveError && <p className="text-sm text-destructive">{saveError}</p>}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? t.appearance.savingCta : t.appearance.saveCta}
          </Button>
          {saved && <span className="text-sm text-muted-foreground">{t.appearance.savedLabel}</span>}
        </div>
      </form>

      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">{t.appearance.livePreviewHeading}</h2>
        <WidgetPreview
          publicId={bot.publicId}
          color={color}
          botName={botName}
          welcome={welcome}
          quickQuestions={quickQuestions}
          showsBadge={
            badgeRequired === undefined ? undefined : resolveShowsBadge({ badgeRequired, showBadge })
          }
          language={language}
          remountKey={previewKey}
        />
        <p className="text-xs text-muted-foreground">
          {t.appearance.previewCaption}
          {/* The build instructions below are for us, not for bot owners: a customer on
              app.embent.com was being told to run `bun run build` in a repo they do not have
              (spotted while screenshotting the page, 2026-08-07). Dev builds only. */}
          {import.meta.env.DEV ? (
            <>
              {' '}
              {t.appearance.devHintPrefix}{' '}
              <code className="rounded bg-muted px-1 py-0.5">bun run build</code> {t.appearance.devHintIn}{' '}
              <code className="rounded bg-muted px-1 py-0.5">apps/widget</code> {t.appearance.devHintAndCopy}{' '}
              <code className="rounded bg-muted px-1 py-0.5">dist/widget.js</code> {t.appearance.devHintTo}{' '}
              <code className="rounded bg-muted px-1 py-0.5">apps/api/public/widget.js</code>.
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
}
